import React from 'react';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import { b } from 'utils/i18nUtils';

import { ButtonType } from '../../ui/Button';
import { Button, Confirm, Icon } from '../../ui';

interface EmergencyAlertProps {
  isActivated: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}

const EmergencyAlert: React.FC<EmergencyAlertProps> = ({ isActivated, onChange, disabled }) => {
  const [isModalVisible, setIsModalVisible] = React.useState(false);

  const handleCloseModal = () => setIsModalVisible(false);

  const handleConfirm = () => {
    onChange(true);
    handleCloseModal();
  };

  const handleChangeMode = () => {
    if (!isActivated) {
      setIsModalVisible(true);
    } else {
      onChange(false);
    }
  };

  return (
    <>
      <div
        className={classNames(
          'flex justify-between items-center w-full bg-card-bg border border-default rounded-admin-card p-4',
          { 'bg-danger-lowest! border-danger-low!': isActivated },
        )}
      >
        <div className="flex items-center">
          <div className={classNames('relative bg-brand-high rounded-full size-20 flex items-center justify-center', { 'bg-danger-default!': isActivated })}>
            <Icon className="fb-icon--warning text-2xl kiosk:text-5xl text-white" />
          </div>
          <div className="flex flex-col items-start justify-between ml-5 h-20">
            <h1 className={classNames('text-4xl! uppercase text-default', { 'text-danger-default!': isActivated })}>
              {isActivated
                ? <FormattedMessage id="warning" defaultMessage="Warning" />
                : <FormattedMessage id="admin.emergencyAlert.title" defaultMessage="Emergency access" />}
            </h1>
            <p className={classNames('text-xl! text-default font-medium', { 'text-danger-default!': isActivated })}>
              <FormattedMessage
                id="admin.emergencyAlert.subtitle"
                defaultMessage="Only use this mode in an emergency"
              />
            </p>
          </div>
        </div>
        <Button
          disabled={disabled}
          onClick={handleChangeMode}
          testId="changeEmergencyMode"
          type={isActivated ? ButtonType.ERROR : ButtonType.TERTIARY}
          className="text-xl! leading-10! font-semibold w-56 p-6! uppercase transition-none"
        >
          {isActivated
            ? <FormattedMessage id="exit" defaultMessage="Exit" />
            : <FormattedMessage id="activate" defaultMessage="Activate" />}
        </Button>
      </div>
      <Confirm
        hideCloseButton
        open={isModalVisible}
        onConfirm={handleConfirm}
        onCancel={handleCloseModal}
        confirmProps={{ type: ButtonType.ERROR }}
        cancelProps={{ type: ButtonType.SECONDARY }}
        title={(
          <h2 className="flex justify-center items-center uppercase">
            <FormattedMessage id="admin.emergencyAlert.title" defaultMessage="Emergency access" />
          </h2>
        )}
        question={(
          <p className="text-center mb-10">
            <FormattedMessage
              id="admin.emergencyAlert.confirmModal.description"
              defaultMessage="Confirm that you want to <b>activate the emergency mode.</b>"
              values={{ b }}
            />
          </p>
        )}
      />
    </>
  );
};

export default EmergencyAlert;
